import React, { useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { formatDateTime } from '../../utils/shortcuts';
import './FTLStats.css'; // Add a custom CSS file for styling
import Skeleton from 'react-loading-skeleton';
import moment from 'moment';
import html2canvas from 'html2canvas';

export default function FTLStats({ data, heading, isLoading, provider }) {
    let navigate = useNavigate();
    const componentRef = useRef(null);
    const buttonRef = useRef(null);
    const timestampRef = useRef(null);

    const handleDownloadScreenshot = async () => {
        if (componentRef.current && buttonRef.current && timestampRef.current) {
            try {
                // Hide the button and show timestamp before taking screenshot
                buttonRef.current.style.display = 'none';
                timestampRef.current.style.display = 'block';

                // Wait a brief moment for the DOM to update
                await new Promise(resolve => setTimeout(resolve, 100));

                const canvas = await html2canvas(componentRef.current, {
                    backgroundColor: '#ffffff',
                    scale: 2, // Higher resolution
                    logging: false,
                    useCORS: true,
                    allowTaint: false
                });

                // Show the button and hide timestamp again
                buttonRef.current.style.display = 'block';
                timestampRef.current.style.display = 'none';

                // Convert canvas to image
                const image = canvas.toDataURL('image/jpeg', 0.95);

                // Create download link
                const link = document.createElement('a');
                link.download = `ftl-stats-${moment().format('YYYY-MM-DD-HH-mm-ss')}.jpg`;
                link.href = image;

                // Trigger download
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } catch (error) {
                console.error('Error generating screenshot:', error);
                alert('Failed to generate screenshot. Please try again.');

                // Make sure to show the button and hide timestamp again even if there's an error
                if (buttonRef.current) {
                    buttonRef.current.style.display = 'block';
                }
                if (timestampRef.current) {
                    timestampRef.current.style.display = 'none';
                }
            }
        }
    };
    if (!data && isLoading) {
        // Render skeleton loader
        return (
            <div className="analytics-container">
                <h3 className="analytics-heading">
                    <Skeleton width={200} />
                </h3>
                <p className="analytics-subheading">
                    <Skeleton width={300} />
                </p>
                <div className="analytics-table-container tableCus">
                    <table className="analytics-table ">
                        <thead>
                            <tr>
                                {Array(9)
                                    .fill(null)
                                    .map((_, index) => (
                                        <th key={index}>
                                            <Skeleton width={100} />
                                        </th>
                                    ))}
                            </tr>
                        </thead>
                        <tbody>
                            {Array(5)
                                .fill(null)
                                .map((_, rowIndex) => (
                                    <tr key={rowIndex}>
                                        {Array(9)
                                            .fill(null)
                                            .map((_, colIndex) => (
                                                <td key={colIndex}>
                                                    <Skeleton width={80} />
                                                </td>
                                            ))}
                                    </tr>
                                ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    // Calculate totals for each column
    const totals = data.reduce(
        (acc, item) => {
            acc.total += item.stats.total;
            acc.review += item.stats.review;
            acc.pending += item.stats.pending;
            acc.hold += item.stats.hold;
            acc.resolved += item.stats.resolved;
            acc.cancelled += item.stats.cancelled;
            acc.closed += item.stats.closed;
            return acc;
        },
        { total: 0, review: 0, pending: 0, hold: 0, resolved: 0, cancelled: 0, closed: 0 }
    );

    return (
        <div className="analytics-container" ref={componentRef}>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                    <h3 className="analytics-heading">{heading}</h3>
                    <p className="analytics-subheading">Last updated at {moment(data[0]?.updated_at).tz('Asia/Kolkata').fromNow()}</p>
                    <p
                        ref={timestampRef}
                        className="analytics-subheading mb-0"
                        style={{ display: 'none' }}
                    >
                        <small className="text-muted">Generated on: {moment().tz('Asia/Kolkata').format('MMMM DD, YYYY [at] HH:mm:ss')}</small>
                    </p>
                </div>
                <button
                    ref={buttonRef}
                    onClick={handleDownloadScreenshot}
                    className="btn btn-outline-primary d-flex align-items-center gap-1"
                    style={{ fontSize: '10px', padding: '4px 8px' }}
                    title="Download Screenshot"
                >
                    <span className="material-icons" style={{ fontSize: '12px' }}>photo_camera</span>
                    <span>Shot</span>
                </button>
            </div>
            <div className="analytics-table-container tableCus">
                <table className="analytics-table popOverTable">
                    <thead>
                        <tr>
                            <th>Name</th>
                            {/* <th>Total</th>
                            <th>Review</th> */}
                            <th>Pending</th>
                            {/* <th>Hold</th>
                            <th>Resolved</th>
                            <th>Cancelled</th>
                            <th>Closed</th>
                            <th>Total</th> */}
                        </tr>
                    </thead>
                    <tbody>
                        {data.map((item) => {
                            const rowTotal =
                                item.stats.total +
                                item.stats.review +
                                item.stats.pending +
                                item.stats.hold +
                                item.stats.resolved +
                                item.stats.cancelled +
                                item.stats.closed;

                            return (
                                <tr key={item.id}>
                                    <td>
                                        <Link to={`/docket?fiber_team_leader=${item.id}`}>{item.name}</Link>
                                    </td>
                                    {/* <td>{item.stats.total}</td> */}
                                    {/* <td>{item.stats.review}</td> */}
                                    <td
                                        style={{
                                            backgroundColor: (item.stats.pending + item.stats.review + item.stats.hold) === 0 ? '#6d976d' : '#f69648',
                                            color: (item.stats.pending + item.stats.review + item.stats.hold) === 0 ? 'white' : 'inherit',
                                        }}
                                        className='fw-bold'
                                    >
                                        {item.stats.pending + item.stats.review + item.stats.hold}
                                    </td>
                                    {/* <td>{item.stats.hold}</td> */}
                                    {/* <td>{item.stats.resolved}</td> */}
                                    {/* <td>{item.stats.cancelled}</td> */}
                                    {/* <td>{item.stats.closed}</td> */}
                                    {/* <td>{rowTotal}</td> */}
                                    <div className='tooltipText'>
                                        <div className='toolTipInerDiv shadow'>
                                            <h6 className='m-0'>{item.name}</h6>
                                            <p className='m-0'><small>Total: {item.stats.total}</small></p>
                                            <p className='m-0'><small>Review: {item.stats.review}</small></p>
                                            <p className='m-0'><small>Pending: {item.stats.pending}</small></p>
                                            <p className='m-0'><small>Hold: {item.stats.hold}</small></p>
                                            <p className='m-0'><small>Resolved: {item.stats.resolved}</small></p>
                                            <p className='m-0'><small>Cancelled: {item.stats.cancelled}</small></p>
                                            <p className='m-0'><small>Closed: {item.stats.closed}</small></p>
                                            <p className='m-0'><small>Total: {rowTotal}</small></p>

                                        </div>
                                    </div>
                                </tr>

                            );
                        })}
                    </tbody>
                    <tfoot>
                        <tr>
                            <th>Total</th>
                            {/* <th>{totals.total}</th> */}
                            {/* <th>{totals.review}</th> */}
                            <th>{totals.pending + totals.review + totals.hold}</th>
                            {/* <th>{totals.hold}</th> */}
                            {/* <th>{totals.resolved}</th> */}
                            {/* <th>{totals.cancelled}</th> */}
                            {/* <th>{totals.closed}</th> */}
                            {/* <th>
                                {totals.total +
                                    totals.review +
                                    totals.pending +
                                    totals.hold +
                                    totals.resolved +
                                    totals.cancelled +
                                    totals.closed}
                            </th> */}
                        </tr>
                    </tfoot>
                </table>
            </div>
        </div>
    );
}